import React, { useState, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import { io } from "socket.io-client";
import Div100vh from "react-div-100vh";
import config from "@/config";
import { fetchData, handleAndTrackError } from "@/lib/helper";
import HomeFaqWhatsappHeader from "@/components/common/Header/HomeFaqWhatsappHeader.jsx";
import StepCard from "./StepCard";
import FexPrimeLogo from "@/assets/icons/poweredbyfexprime.svg";
import { isNeutralLender, useLender } from "@/lib/lender";
import { useNavigate } from "react-router-dom";
import myGif from "@/assets/icons/animated-logo-new.gif";
import ProcessingScreen from "@/b2b/pages/processing";

export default function Home() {
  const [data, setData] = useState([]);
  const [userData, setUserData] = useRecoilState(userDataAtom);
  const lender = useLender(userData?.lender);
  const [isLoading, setIsLoading] = useState(false);
  const socket = useRef(null);
  const [isFetched, setIsFetched] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const navigate = useNavigate();

  let kycData, bankingData, pennyDropData, esignData, emandateData;

  useEffect(() => {
    // Fetch journey data
    const getData = async () => {
      try {
        setIsLoading(true);
        let journeyData = await fetchData("journey");

        let journeyDataSteps = journeyData?.journey;

        kycData = journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "KYC"
        );
        bankingData = journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "BANKING"
        );
        pennyDropData = journeyDataSteps.find(
          (step) => step.actor === "SYSTEM" && step.name === "PENNY_DROP"
        );
        esignData = journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "E_SIGN"
        );
        emandateData = journeyDataSteps.find(
          (step) => step.actor === "SYSTEM" && step.name === "EMANDATE"
        );

        if (emandateData.status === "SUCCESS") navigate("/");
        else if (
          kycData.status === "SUCCESS" &&
          bankingData.status === "SUCCESS" &&
          pennyDropData.status === "SUCCESS" &&
          esignData.status === "SUCCESS"
        ) {
          navigate("/mandate");
        } else if (
          bankingData.status === "SUCCESS" &&
          pennyDropData.status === "PENDING"
        ) {
          navigate("/penny-drop");
        }

        setData(journeyDataSteps?.filter((step) => step.actor === "USER"));
      } catch (error) {
        handleAndTrackError(error, {
          context: "Fetching journey data",
          endpoint: "journey",
        });
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const data = await fetchData("user/me");
        setUserData(data);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    // Check if data should be fetched
    if (
      !isFetched && // Check fetch flag
      (!userData?.phone || userData.approval?.length === 0) // Validate condition
    ) {
      getUserData();
      setIsFetched(true); // Prevent further fetching
    }

    const timeoutId = setTimeout(() => {
      setTimeoutReached(true);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [setUserData, userData?.phone, userData?.approval, isFetched]);

  // Recoverable: this covers a `fetchData("journey")` read only. Deliberately NOT enabled on the
  // sibling bank_details screen, where the same component covers `POST banking/start` and a reload
  // could open a second session and burn one of the tenant's limited attempts.
  if (isLoading) return <ProcessingScreen recoverable />;
  return (
    <Div100vh className="flex flex-col justify-between py-10 gap-8 overflow-y-scroll bg-white">
      <HomeFaqWhatsappHeader />
      {data ? (
        <div className="flex flex-col">
          {data.map((cardData, index) => (
            <React.Fragment key={index}>
              <StepCard data={cardData} setIsLoading={setIsLoading} />
              {index < data.length - 1 && (
                <div className="w-px h-16 border-l-2 border-dashed border-gray-400 ml-8"></div>
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="w-full flex aspect-square items-center justify-center">
          <img src={myGif} alt="Running GIF" className="max-w-32" />
        </div>
      )}
      {userData.lender &&
        (userData.lender === "FEXPRIME" ? (
          <div className="flex flex-col items-center justify-center ">
            <img src={FexPrimeLogo} alt="FexPrime Logo" className="" />
          </div>
        ) : isNeutralLender(lender) ? null : (
          <div className="flex flex-col items-center justify-center ">
            <img
              src={lender.logoUrl}
              alt={lender.legalName}
              className="max-h-10"
            />
          </div>
        ))}
    </Div100vh>
  );
}
